import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  adsListBrands,
  adsListMonitors,
  adsCreateMonitor,
  adsPauseMonitor,
  adsResumeMonitor,
  adsRunMonitorNow,
  adsDeleteMonitor,
  adsListMonitorRuns,
  adsMonitorCostTrend,
  adsListCompetitors,
} from "../services/api";

// ── ADS Intelligence - Monitors page (E4.1 / E4.2 / E4.3) ──
// Self-contained: list of scheduled monitors + a builder modal + an
// expandable per-monitor detail showing run history and cost.

const GOLD = "#d4af37";
const INK = "#0b1020";
const PANEL = "#11162a";

const PROVIDERS = [
  { key: "meta", label: "Meta", enabled: true },
  { key: "google", label: "Google", enabled: true },
  { key: "tiktok", label: "TikTok", enabled: true },
];

const SCHEDULE_OPTIONS = [
  { value: "manual", label: "Manual only" },
  { value: "hourly", label: "Hourly" },
  { value: "daily", label: "Daily (09:00)" },
  { value: "three_times_weekly", label: "3× / week (Mon/Wed/Fri)" },
  { value: "weekly", label: "Weekly (Mon 09:00)" },
  { value: "biweekly", label: "Biweekly (1st & 15th)" },
  { value: "monthly", label: "Monthly (1st)" },
];

// Map a stored cron back to a friendly label for the table.
const CRON_LABELS = {
  "0 * * * *": "Hourly",
  "0 9 * * *": "Daily 09:00",
  "0 9 * * 1,3,5": "3×/week",
  "0 9 * * 1": "Weekly Mon",
  "0 9 1,15 * *": "Biweekly",
  "0 9 1 * *": "Monthly",
};

function humanizeCron(cron) {
  if (!cron) return "Manual";
  return CRON_LABELS[cron] || cron;
}

function fmtDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const STATUS_COLORS = {
  completed: "#2ecc71",
  partial: "#f1c40f",
  failed: "#e74c3c",
  running: "#3498db",
  queued: "#95a5a6",
  aborted: "#7f8c8d",
};

function StatusDot({ status }) {
  return (
    <span style={{
      display: "inline-block", width: 8, height: 8, borderRadius: "50%",
      background: STATUS_COLORS[status] || "#888", marginRight: 6,
    }} />
  );
}

export default function AdsMonitorsPage({ currentUser }) {
  const nav = useNavigate();
  const [brands, setBrands] = useState([]);
  const [monitors, setMonitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [b, m] = await Promise.all([adsListBrands(), adsListMonitors()]);
      setBrands(b.items || []);
      setMonitors(m.items || []);
    } catch (e) {
      setErr(e.message || "Failed to load monitors");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const brandName = useCallback(
    (id) => (brands.find((b) => b.id === id) || {}).name || `Brand ${id}`,
    [brands]
  );

  const onPauseResume = async (mon) => {
    setBusyId(mon.id);
    try {
      if (mon.is_active) await adsPauseMonitor(mon.id);
      else await adsResumeMonitor(mon.id);
      await load();
    } catch (e) { setErr(e.message); } finally { setBusyId(null); }
  };

  const onRunNow = async (mon) => {
    setBusyId(mon.id);
    try {
      await adsRunMonitorNow(mon.id);
      setExpandedId(mon.id);
      await load();
    } catch (e) { setErr(e.message); } finally { setBusyId(null); }
  };

  const onDelete = async (mon) => {
    if (!window.confirm(`Delete monitor "${mon.name}"? This stops all future runs.`)) return;
    setBusyId(mon.id);
    try { await adsDeleteMonitor(mon.id); await load(); }
    catch (e) { setErr(e.message); } finally { setBusyId(null); }
  };

  return (
    <div style={{ minHeight: "100vh", background: INK, color: "#e8e8ef", padding: "24px 32px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <button onClick={() => nav("/ads")} style={btnGhost}>← Back to Ads</button>
          <h1 style={{ margin: "10px 0 2px", fontSize: 26, fontWeight: 900 }}>
            Monitors <span style={{ color: GOLD }}>·</span> Competitive Intelligence
          </h1>
          <div style={{ color: "#8a90a6", fontSize: 13 }}>
            Scheduled ad fetching across providers, with automatic visual extraction.
          </div>
        </div>
        <button onClick={() => setBuilderOpen(true)} style={btnPrimary}>+ Create monitor</button>
      </div>

      {err && <div style={errBox}>{err}</div>}

      {loading ? (
        <div style={{ color: "#8a90a6", padding: 40, textAlign: "center" }}>Loading monitors…</div>
      ) : monitors.length === 0 ? (
        <div style={{ ...PanelStyle, padding: 40, textAlign: "center", color: "#8a90a6" }}>
          No monitors yet. Click <b style={{ color: GOLD }}>+ Create monitor</b> to schedule one.
        </div>
      ) : (
        <div style={PanelStyle}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ color: "#8a90a6", fontSize: 12, textAlign: "left" }}>
                <th style={th}>Name</th><th style={th}>Brand</th><th style={th}>Providers</th>
                <th style={th}>Schedule</th><th style={th}>Next run</th><th style={th}>Last run</th>
                <th style={th}>Status</th><th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {monitors.map((m) => (
                <React.Fragment key={m.id}>
                  <tr style={{ borderTop: "1px solid #1e2440" }}>
                    <td style={td}>
                      <button onClick={() => setExpandedId(expandedId === m.id ? null : m.id)} style={linkBtn}>
                        {expandedId === m.id ? "▾" : "▸"} {m.name}
                      </button>
                    </td>
                    <td style={td}>{brandName(m.brand_id)}</td>
                    <td style={td}>{(m.providers || []).map((p) => <Chip key={p}>{p}</Chip>)}</td>
                    <td style={td}>{humanizeCron(m.schedule_cron)}</td>
                    <td style={td}>{fmtDate(m.next_run_at)}</td>
                    <td style={td}>{fmtDate(m.last_run_at)}</td>
                    <td style={td}>
                      <span style={{ color: m.is_active ? "#2ecc71" : "#7f8c8d" }}>
                        {m.is_active ? "● Active" : "❚❚ Paused"}
                      </span>
                    </td>
                    <td style={td}>
                      <button disabled={busyId === m.id} onClick={() => onRunNow(m)} style={btnTiny}>Run now</button>
                      <button disabled={busyId === m.id} onClick={() => onPauseResume(m)} style={btnTiny}>
                        {m.is_active ? "Pause" : "Resume"}
                      </button>
                      <button disabled={busyId === m.id} onClick={() => onDelete(m)} style={{ ...btnTiny, color: "#e74c3c" }}>
                        Delete
                      </button>
                    </td>
                  </tr>
                  {expandedId === m.id && (
                    <tr>
                      <td colSpan={8} style={{ background: "#0d1226", padding: 0 }}>
                        <MonitorDetail monitorId={m.id} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {builderOpen && (
        <MonitorBuilder
          brands={brands}
          onClose={() => setBuilderOpen(false)}
          onCreated={async () => { setBuilderOpen(false); await load(); }}
        />
      )}
    </div>
  );
}

// ── Inline detail: run history + cost (E4.3) ──
function MonitorDetail({ monitorId }) {
  const [runs, setRuns] = useState(null);
  const [cost, setCost] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [r, c] = await Promise.all([
          adsListMonitorRuns(monitorId, 20),
          adsMonitorCostTrend(monitorId),
        ]);
        if (alive) { setRuns(r.items || []); setCost(c); }
      } catch { if (alive) setRuns([]); }
    })();
    return () => { alive = false; };
  }, [monitorId]);

  if (runs === null) return <div style={{ padding: 16, color: "#8a90a6" }}>Loading runs…</div>;
  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: "flex", gap: 24, marginBottom: 12, fontSize: 13 }}>
        <span style={{ color: "#8a90a6" }}>Total cost: <b style={{ color: GOLD }}>${(cost?.total_cost_usd ?? 0).toFixed(4)}</b></span>
        <span style={{ color: "#8a90a6" }}>Runs: <b style={{ color: "#e8e8ef" }}>{runs.length}</b></span>
      </div>
      {runs.length === 0 ? (
        <div style={{ color: "#8a90a6" }}>No runs yet. Use “Run now” to fire one.</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ color: "#8a90a6", textAlign: "left" }}>
              <th style={th}>Started</th><th style={th}>Status</th><th style={th}>Trigger</th>
              <th style={th}>Entities</th><th style={th}>Ads fetched</th><th style={th}>New</th>
              <th style={th}>Extraction</th><th style={th}>Cost</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((r) => (
              <tr key={r.id} style={{ borderTop: "1px solid #1e2440" }}>
                <td style={td}>{fmtDate(r.started_at)}</td>
                <td style={td}><StatusDot status={r.status} />{r.status}</td>
                <td style={td}>{r.triggered_by}</td>
                <td style={td}>{r.entities_succeeded}/{r.entities_planned} ok{r.entities_failed ? `, ${r.entities_failed} failed` : ""}</td>
                <td style={td}>{r.ads_fetched_total}</td>
                <td style={td}>{r.ads_new_total > 0 ? <b style={{ color: GOLD }}>+{r.ads_new_total}</b> : 0}</td>
                <td style={td}>{r.extraction_status}</td>
                <td style={td}>${(r.cost_usd ?? 0).toFixed(4)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Builder modal (E4.2) ──
function MonitorBuilder({ brands, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [brandId, setBrandId] = useState(brands[0]?.id || "");
  const [providers, setProviders] = useState(["meta"]);
  const [schedule, setSchedule] = useState("weekly");
  const [tz, setTz] = useState("Europe/Bucharest");
  const [includeSelf, setIncludeSelf] = useState(true);
  const [competitors, setCompetitors] = useState([]);
  const [selectedCompetitors, setSelectedCompetitors] = useState([]);
  const [countries, setCountries] = useState("RO");
  const [maxAds, setMaxAds] = useState(100);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!brandId) return;
    adsListCompetitors(brandId).then((c) => setCompetitors(c.items || c || [])).catch(() => setCompetitors([]));
  }, [brandId]);

  const toggleProvider = (key) =>
    setProviders((ps) => (ps.includes(key) ? ps.filter((p) => p !== key) : [...ps, key]));
  const toggleCompetitor = (id) =>
    setSelectedCompetitors((cs) => (cs.includes(id) ? cs.filter((c) => c !== id) : [...cs, id]));

  const canSave = name.trim() && brandId && providers.length > 0 && !saving;

  const submit = async () => {
    setSaving(true); setErr(null);
    try {
      await adsCreateMonitor({
        brand_id: Number(brandId),
        name: name.trim(),
        providers,
        included_competitor_ids: selectedCompetitors,
        include_self: includeSelf,
        countries: countries.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean),
        schedule,
        schedule_timezone: tz,
        max_ads_per_run: Number(maxAds) || 100,
      });
      await onCreated();
    } catch (e) {
      setErr(e.message || "Failed to create monitor");
      setSaving(false);
    }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginTop: 0, fontSize: 20 }}>Create monitor</h2>
        {err && <div style={errBox}>{err}</div>}

        <label style={lbl}>Name</label>
        <input style={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Temu Romania - weekly" />

        <label style={lbl}>Brand</label>
        <select style={input} value={brandId} onChange={(e) => setBrandId(e.target.value)}>
          {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>

        <label style={lbl}>Providers</label>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {PROVIDERS.map((p) => (
            <button key={p.key} disabled={!p.enabled}
              onClick={() => p.enabled && toggleProvider(p.key)}
              style={{
                ...chipBtn,
                opacity: p.enabled ? 1 : 0.4,
                background: providers.includes(p.key) ? GOLD : "transparent",
                color: providers.includes(p.key) ? INK : "#e8e8ef",
                cursor: p.enabled ? "pointer" : "not-allowed",
              }}>
              {p.label}{!p.enabled && " (soon)"}
            </button>
          ))}
        </div>

        <label style={lbl}>Coverage</label>
        <label style={{ ...checkRow }}>
          <input type="checkbox" checked={includeSelf} onChange={(e) => setIncludeSelf(e.target.checked)} />
          &nbsp;Include the brand itself
        </label>
        {competitors.length > 0 && (
          <div style={{ marginTop: 6 }}>
            <div style={{ color: "#8a90a6", fontSize: 12, marginBottom: 4 }}>Competitors to include:</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {competitors.map((c) => {
                const cid = c.competitor_brand_id ?? c.id;
                const label = c.competitor_name ?? c.name ?? `Brand ${cid}`;
                return (
                  <button key={cid} onClick={() => toggleCompetitor(cid)}
                    style={{
                      ...chipBtn,
                      background: selectedCompetitors.includes(cid) ? GOLD : "transparent",
                      color: selectedCompetitors.includes(cid) ? INK : "#e8e8ef",
                    }}>
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={lbl}>Schedule</label>
            <select style={input} value={schedule} onChange={(e) => setSchedule(e.target.value)}>
              {SCHEDULE_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label style={lbl}>Timezone</label>
            <input style={input} value={tz} onChange={(e) => setTz(e.target.value)} />
          </div>
        </div>

        <div style={{ display: "flex", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <label style={lbl}>Countries (ISO, comma-sep)</label>
            <input style={input} value={countries} onChange={(e) => setCountries(e.target.value)} placeholder="RO, HU" />
          </div>
          <div style={{ flex: 1 }}>
            <label style={lbl}>Max ads / run</label>
            <input style={input} type="number" value={maxAds} onChange={(e) => setMaxAds(e.target.value)} />
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18 }}>
          <button onClick={onClose} style={btnGhost}>Cancel</button>
          <button onClick={submit} disabled={!canSave} style={{ ...btnPrimary, opacity: canSave ? 1 : 0.5 }}>
            {saving ? "Creating…" : "Create monitor"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Chip({ children }) {
  return (
    <span style={{
      display: "inline-block", padding: "1px 7px", margin: "0 3px 0 0",
      borderRadius: 10, background: "#1e2440", fontSize: 11, color: "#cdd2e6",
    }}>{children}</span>
  );
}

// ── styles ──
const PanelStyle = { background: PANEL, borderRadius: 12, border: "1px solid #1e2440", overflow: "hidden" };
const th = { padding: "10px 12px", fontWeight: 600 };
const td = { padding: "10px 12px", fontSize: 13, verticalAlign: "top" };
const lbl = { display: "block", margin: "12px 0 4px", fontSize: 12, color: "#8a90a6" };
const input = { width: "100%", boxSizing: "border-box", padding: "8px 10px", borderRadius: 8, border: "1px solid #2a3158", background: INK, color: "#e8e8ef" };
const checkRow = { display: "flex", alignItems: "center", fontSize: 13, color: "#cdd2e6" };
const btnPrimary = { padding: "9px 16px", borderRadius: 8, border: `1px solid ${GOLD}`, background: GOLD, color: INK, fontWeight: 700, cursor: "pointer" };
const btnGhost = { padding: "7px 12px", borderRadius: 8, border: "1px solid #2a3158", background: "transparent", color: "#cdd2e6", cursor: "pointer" };
const btnTiny = { padding: "4px 8px", marginRight: 6, borderRadius: 6, border: "1px solid #2a3158", background: "transparent", color: "#cdd2e6", cursor: "pointer", fontSize: 12 };
const linkBtn = { background: "none", border: "none", color: "#e8e8ef", cursor: "pointer", fontSize: 13, fontWeight: 600, padding: 0 };
const chipBtn = { padding: "5px 12px", borderRadius: 16, border: `1px solid ${GOLD}`, cursor: "pointer", fontSize: 13 };
const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 };
const modal = { background: PANEL, border: "1px solid #2a3158", borderRadius: 14, padding: 24, width: 560, maxWidth: "92vw", maxHeight: "88vh", overflowY: "auto" };
const errBox = { background: "#3a1620", border: "1px solid #e74c3c", color: "#ffb3bd", padding: "8px 12px", borderRadius: 8, margin: "8px 0", fontSize: 13 };
